import { useQuery } from "@apollo/client";
import { useRecoilValue } from "recoil";
import { useMemo } from "react";

import { MATCHES, PLAYER_MOMENTS } from "MVVM/GraphQL/matchHistory";
import { ActiveOverlayPopUpState } from "MVVM/State/iframe";

export interface VictimData {
  avatar: string;
}

export interface Player {
  avatar: string;
  gameId: string;
  displayName: string;
}

export interface Moment {
  id: string;
  title: string;
  timelineLabel: string;
  victimData: VictimData[];
  icons: string[];
  player: Player;
  match: {
    id: string;
  };
  game: string;
}

export interface Match {
  id: string;
  title: string;
}

export interface MatchesData {
  matches: {
    nodes: Match[];
  };
}

export interface MatchesVariables {
  playerGameId: string;
  first?: number;
}

export interface MomentsData {
  moments: Moment[];
}

export interface MomentsVariables {
  playerGameId: string;
  matchId?: string;
  sort?: string;
  limit?: number;
  type?: string;
}

export const MatchHistoryWidgetViewModel = ({
  playerGameId,
}: {
  playerGameId: string;
}) => {
  const activeOverlayPopUp = useRecoilValue(ActiveOverlayPopUpState);

  const {
    data: matchesData,
    loading: matchesLoading,
    error: matchesError,
  } = useQuery<MatchesData, MatchesVariables>(MATCHES, {
    variables: {
      playerGameId: playerGameId,
      first: 1,
    },
    skip:
      activeOverlayPopUp !== "history" &&
      activeOverlayPopUp !== "nextUpPlaylist",
  });

  const latestMatchId = useMemo(() => {
    return matchesData?.matches?.nodes?.[0]?.id || "";
  }, [matchesData]);

  const matchesQueryExecuted = useMemo(() => {
    return (
      !matchesLoading &&
      (matchesData !== undefined || matchesError !== undefined)
    );
  }, [matchesLoading, matchesData, matchesError]);

  const {
    data: momentsData,
    loading: momentsLoading,
    error: momentsError,
  } = useQuery<MomentsData, MomentsVariables>(PLAYER_MOMENTS, {
    variables: {
      playerGameId: playerGameId,
      ...(latestMatchId && { matchId: latestMatchId }),
      sort: "MATCH",
      type: "PRIMARY_ONLY",
      limit: 3,
    },
    skip:
      (activeOverlayPopUp !== "history" &&
        activeOverlayPopUp !== "nextUpPlaylist") ||
      !matchesQueryExecuted,
  });

  const moments = useMemo(() => {
    const allMoments = momentsData?.moments || [];
    return allMoments.slice(0, 3);
  }, [momentsData]);

  const loading = matchesLoading || momentsLoading;

  const error = matchesError || momentsError;

  return {
    moments,
    loading,
    error,
  };
};
